import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import {
  Button,
  DetailsTable,
  ImageCarousel,
} from "@swordsweeper/ssi-ui-component-library";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import * as ProductDetailApi from "@src/api/ProductDetailApi";
import * as ProductApi from "@src/api/ProductApi";
import { addStaticRootToUrl } from "@src/utils/static";
import styles from "./ProductDetailPage.module.scss";
import ProductBreadcrumbs from "@src/components/widgets/ProductBreadcrumbs";
import { VariantChipSelector } from "@src/components/widgets/VariantChipSelector";
import { computeAvailableOptions } from "./variantCascadingUtils";
import filter from "lodash/filter";
import orderBy from "lodash/orderBy";
import kebabCase from "lodash/kebabCase";
import uniqBy from "lodash/uniqBy";
import ProductDescription from "@src/components/widgets/ProductDescription";
import { useSavedForLaterStore } from "@src/state/savedForLaterState";
import { useCurrentUserStore } from "@src/state/currentUserState";
import RequiresRoleContainer from "@src/components/containers/RequiresRoleContainer";
import Roles from "@src/constants/Roles";
import {
  DistributorApiHealthStatus,
  DistributorApiHealthStatuses,
} from "@src/constants/DistributorApiHealthStatuses";
import { DistributorConnectionStatuses } from "@src/components/widgets/AccountDistributorSettings/DistributorConnectionConstants";
import {
  DistributorConnection,
  CurrentDistributorApiHealthStatus,
} from "@src/state/currentUserState";
import { getAddToCartButtonDetails } from "@src/utils/product";
import FavoriteHeartButton from "@src/components/common/FavoriteHeartButton/FavoriteHeartButton";
import SectionHeader from "@src/components/widgets/SectionHeader";
import ProductListRow from "@src/components/widgets/ProductListRow";
import { Product } from "@src/types/product";
import ProductPricingTable from "@src/components/widgets/ProductPricingTable";
import map from "lodash/map";
import { getParamString } from "@src/hooks/useQuery";

type ProductDetail = ProductDetailApi.ProductDetail;
type ProductVariantsResponse = ProductDetailApi.ProductVariantsResponse;
type DistributorProduct = ProductDetailApi.DistributorProduct;
type SiblingGroupsResponse = ProductDetailApi.SiblingGroupsResponse;

type PricingRow = {
  id: string;
  distributorSlug: string;
  isPreferred?: boolean;
  availabilityStatus: string;
  shippingText?: string;
  rewardsLabel?: string;
  pricePerUnit?: number;
  price: number | undefined;
  quantity?: number;
  hasMorePrices: boolean;
  priceBreaksCount: number;
  minQuantity: number;
  webEligible: boolean;
  buttonText: string;
  buttonDisabled: boolean;
  originalData: DistributorProduct;
  isConnected?: boolean;
  distributorApiHealthStatus: DistributorApiHealthStatus | undefined;
};

function hasDistributorProducts(p: ProductDetail): p is ProductDetail {
  return "distributorProducts" in p;
}

function mapDistributorProductToTableRow(
  distributorProduct: DistributorProduct,
  distributorApiHealthStatuses: CurrentDistributorApiHealthStatus[] | null,
  getDistributorConnection: (
    distributorSlug: string,
  ) => DistributorConnection | undefined,
): PricingRow {
  const livePricing = distributorProduct.livePricing;
  const displayQuantity =
    livePricing?.quantityAvailable ?? distributorProduct.quantity_available;

  const distributorApiHealthStatus = distributorApiHealthStatuses?.find(
    (status) => status.distributor === distributorProduct.distributor.slug,
  )?.status;
  const isDistributorApiHealthStatusHealthy =
    distributorApiHealthStatus === DistributorApiHealthStatuses.Healthy;

  const connection = getDistributorConnection(
    distributorProduct.distributor.slug,
  );
  const isConnectedToDistributor =
    connection?.status === DistributorConnectionStatuses.Connected;

  const hasPerfectApiConnection =
    isConnectedToDistributor && isDistributorApiHealthStatusHealthy;

  const { cartButtonText, cartButtonDisabled } = getAddToCartButtonDetails(
    livePricing,
    hasPerfectApiConnection,
  );

  const availabilityStatus =
    livePricing?.availabilityStatus ||
    (livePricing?.isInStock ? "In Stock" : "Out of Stock");

  return {
    id: distributorProduct.id,
    distributorSlug: distributorProduct.distributor.slug,
    isPreferred: distributorProduct.distributor.isPreferred,
    availabilityStatus,
    shippingText: "2-3 days",
    rewardsLabel: "Greatest Savings",
    pricePerUnit: hasPerfectApiConnection
      ? livePricing?.pricePerUnit
      : undefined,
    price: hasPerfectApiConnection ? livePricing?.price : undefined,
    quantity: displayQuantity,
    hasMorePrices: Boolean(livePricing?.priceBreaks?.length),
    priceBreaksCount: livePricing?.priceBreaks?.length ?? 0,
    minQuantity: livePricing?.minQuantity ?? 1,
    webEligible: livePricing?.webEligible ?? true,
    buttonText: cartButtonText,
    buttonDisabled: cartButtonDisabled,
    originalData: distributorProduct,
    isConnected: hasPerfectApiConnection,
    distributorApiHealthStatus,
  };
}

/**
 * Given a group-level attribute key and the currently-selected group context,
 * return the subset of allValues that have at least one valid sibling group.
 *
 * A value V for key K is "available" when there exists at least one sibling
 * group whose groupAttributes satisfy:
 *   - groupAttributes[K] === V
 *   - For every other currently-selected key J: groupAttributes[J] === selectedContext[J]
 *     (groups that lack attribute J entirely are NOT considered a match — they
 *      represent a different formulation family that doesn't apply here)
 *
 * If no groups have been loaded yet, all values are returned as available.
 */
function computeGroupAttrAvailableOptions(
  key: string,
  allValues: string[],
  selectedContext: Record<string, string>,
  allGroups: SiblingGroupsResponse["groups"],
): string[] {
  if (!allGroups.length) return allValues;
  const otherSelected = Object.fromEntries(
    Object.entries(selectedContext).filter(([k]) => k !== key),
  );
  return allValues.filter((value) =>
    allGroups.some((group) => {
      const attrs = group.groupAttributes ?? {};
      if ((attrs[key] ?? "") !== value) return false;
      for (const [k, v] of Object.entries(otherSelected)) {
        // If the group has this attribute it must match; if absent it's a
        // different family (e.g. Injectable has no "strength") — not a match.
        if (!(k in attrs) || attrs[k] !== v) return false;
      }
      return true;
    }),
  );
}

function getAllProductImages(
  product: ProductDetail,
): Array<{ src: string; alt: string }> {
  const images: Array<{ src: string; alt: string }> = [];

  if (hasDistributorProducts(product)) {
    for (const distributorProduct of product.distributorProducts) {
      if (distributorProduct.images && distributorProduct.images.length > 0) {
        const mainImages = filter(distributorProduct.images, {
          imageType: "main",
        });
        const imagesToUse =
          mainImages.length > 0 ? mainImages : distributorProduct.images;

        for (const image of imagesToUse) {
          images.push({
            src: image.imageUrl,
            alt: `${product.itemName} - ${image.imageType || "Product Image"}`,
          });
        }
      }
    }
  }

  if (images.length === 0 && "imageUrl" in product && product.imageUrl) {
    images.push({
      src: product.imageUrl as string,
      alt: "Product Image",
    });
  }

  if (images.length === 0) {
    images.push({
      src: addStaticRootToUrl("images/placeholders/image_na.png"),
      alt: "No images available",
    });
  }

  return images;
}

export default function ProductDetailPage(): React.JSX.Element | null {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingSimilarProducts, setIsLoadingSimilarProducts] =
    useState<boolean>(false);
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);

  const {
    currentUser,
    loading: isUserLoading,
    distributorApiHealthStatuses,
    getDistributorConnection,
    distributorConnections,
  } = useCurrentUserStore();
  const { savedForLaters, fetchSavedForLater } = useSavedForLaterStore();

  // Sibling groups state (group-level navigation: formulation, strength, etc.)
  const [siblingGroupData, setSiblingGroupData] =
    useState<SiblingGroupsResponse | null>(null);

  // Variant state (within-group selection: count, case_quantity, etc.)
  const [variantData, setVariantData] =
    useState<ProductVariantsResponse | null>(null);
  const [isLoadingVariants, setIsLoadingVariants] = useState<boolean>(false);

  // All selected values — both group-level (formulation, strength) and within-group (count).
  // Initialised from URL query params so arriving from a search tile pre-populates chips.
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string | number>
  >(() => Object.fromEntries(searchParams.entries()));

  // Keys that belong to the group level (populated once siblingGroupData loads).
  const groupAttributeKeys = useMemo(
    () => Object.keys(siblingGroupData?.groupAttributeOptions ?? {}),
    [siblingGroupData],
  );

  // Resolve the current group id dynamically from selectedFilters so it
  // updates immediately when a group-level chip (strength, volume) is clicked
  // without waiting for a sibling-groups re-fetch.
  const currentGroupId = useMemo(() => {
    if (!siblingGroupData?.groups) return null;
    const match = siblingGroupData.groups.find((g) => {
      const attrs = g.groupAttributes ?? {};
      if (Object.keys(attrs).length === 0) return true;
      return Object.entries(attrs).every(
        ([k, v]) => String(selectedFilters[k] ?? "") === String(v),
      );
    });
    return match?.groupId ?? siblingGroupData.currentGroupId ?? null;
  }, [siblingGroupData, selectedFilters]);

  // Split selectedFilters into group-level and within-group parts.
  const selectedGroupContext = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(selectedFilters).filter(([k]) =>
          groupAttributeKeys.includes(k),
        ),
      ) as Record<string, string>,
    [selectedFilters, groupAttributeKeys],
  );

  // Derived values — scoped to within-group variant chips only.
  // Must be declared before withinGroupKeys so that withinGroupKeys can
  // derive its keys from the current group's actual variant options.
  const normalizedFilterOptions = useMemo(() => {
    if (!variantData?.allFilterOptions)
      return {} as Record<string, Array<string | number>>;
    const result: Record<string, Array<string | number>> = {};
    for (const [key, opts] of Object.entries(variantData.allFilterOptions)) {
      const filtered = (opts as Array<string | number>).filter(
        (opt) => opt !== "" && opt != null,
      );
      if (filtered.length > 0) result[key] = filtered;
    }
    return result;
  }, [variantData?.allFilterOptions]);

  // Keys that belong to the within-group variant level.
  // Derived from the current group's variant data so they update correctly
  // when the group changes (e.g. switching from Caplets to Injectable).
  const withinGroupKeys = useMemo(
    () => Object.keys(normalizedFilterOptions),
    [normalizedFilterOptions],
  );

  const selectedWithinGroup = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(selectedFilters).filter(([k]) =>
          withinGroupKeys.includes(k),
        ),
      ),
    [selectedFilters, withinGroupKeys],
  );

  // Attribute keys that are relevant to the currently-selected formulation.
  // Used to suppress group-level chips that don't apply to this formulation
  // (e.g. hide "Volume" when the user is on the Chewable Tablet formulation).
  const relevantGroupAttrKeys = useMemo((): Set<string> | null => {
    if (!siblingGroupData?.groups) return null;
    const selectedFormulation = (selectedFilters.formulation as string) || "";
    if (!selectedFormulation) return null; // no formulation selected → show all
    const matchingGroups = siblingGroupData.groups.filter(
      (g) => (g.groupAttributes?.formulation ?? "") === selectedFormulation,
    );
    const keys = new Set<string>();
    for (const g of matchingGroups) {
      for (const k of Object.keys(g.groupAttributes ?? {})) {
        keys.add(k);
      }
    }
    return keys;
  }, [siblingGroupData?.groups, selectedFilters.formulation]);

  const hasVariantData = useMemo(
    () =>
      Object.keys(normalizedFilterOptions).length > 0 ||
      groupAttributeKeys.length > 0,
    [normalizedFilterOptions, groupAttributeKeys],
  );

  // Whether all within-group chips are resolved (either selected or have no options).
  const allWithinGroupSelected = useMemo(() => {
    if (Object.keys(normalizedFilterOptions).length === 0) return true;
    return Object.keys(normalizedFilterOptions).every((k) => {
      if (k in selectedWithinGroup) return true;
      const { available } = computeAvailableOptions(
        k,
        normalizedFilterOptions[k],
        selectedWithinGroup,
        variantData?.memberVariants ?? [],
      );
      return available.length === 0;
    });
  }, [
    normalizedFilterOptions,
    selectedWithinGroup,
    variantData?.memberVariants,
  ]);

  const matchingMember = useMemo(() => {
    if (
      !variantData?.memberVariants ||
      Object.keys(selectedWithinGroup).length === 0
    )
      return null;
    const entries = Object.entries(selectedWithinGroup);
    const filterKeys = Object.keys(normalizedFilterOptions);
    const unselectedKeys = filterKeys.filter(
      (k) => !(k in selectedWithinGroup),
    );
    return (
      variantData.memberVariants.find(
        (member) =>
          entries.every(
            ([key, value]) =>
              !member[key] || String(member[key]) === String(value),
          ) && unselectedKeys.every((key) => !member[key]),
      ) ?? null
    );
  }, [
    allWithinGroupSelected,
    variantData?.memberVariants,
    normalizedFilterOptions,
    JSON.stringify(selectedWithinGroup),
  ]);

  // Group-level "form" key (drives ProductFormSelector special-casing).
  const groupFormKey = useMemo(
    () =>
      groupAttributeKeys.find((k) => k.toLowerCase().includes("form")) ??
      Object.keys(normalizedFilterOptions).find((k) =>
        k.toLowerCase().includes("form"),
      ),
    [groupAttributeKeys, normalizedFilterOptions],
  );

  const pricingData: DistributorProduct[] = useMemo(() => {
    let data: DistributorProduct[] = [];
    if (hasVariantData) {
      data = variantData?.pricing ?? [];
    } else if (product && hasDistributorProducts(product)) {
      data = product.distributorProducts;
    }

    let filteredData =
      distributorConnections === null
        ? data
        : data.filter((item) => {
            const connection = getDistributorConnection(item.distributor.slug);
            if (!connection) return false;
            return (
              connection.status !== DistributorConnectionStatuses.NotConnected
            );
          });

    const matchingIds = matchingMember?.distributorProductIds as
      | string[]
      | undefined;

    if (matchingIds && matchingIds.length > 0) {
      filteredData = filteredData.filter((item) =>
        matchingIds.includes(item.id),
      );
    }

    return orderBy(
      filteredData,
      [(item) => item.distributor?.isPreferred],
      ["desc"],
    );
  }, [
    hasVariantData,
    variantData?.pricing,
    product,
    getDistributorConnection,
    distributorConnections,
    matchingMember?.distributorProductIds,
  ]);

  const images = useMemo(() => {
    if (allWithinGroupSelected && pricingData.length > 0) {
      const variantImages: Array<{ src: string; alt: string }> = [];
      for (const distributorProduct of pricingData) {
        if (distributorProduct.images && distributorProduct.images.length > 0) {
          const mainImages = filter(distributorProduct.images, {
            imageType: "main",
          });
          const imagesToUse =
            mainImages.length > 0 ? mainImages : distributorProduct.images;
          for (const image of imagesToUse) {
            variantImages.push({
              src: image.imageUrl,
              alt: `${pricingData[0].productItemName || product?.itemName || "Product"} - ${
                image.imageType || "Product Image"
              }`,
            });
          }
        }
      }
      if (variantImages.length > 0)
        return uniqBy(variantImages, ["alt", "src"]);
      return [
        {
          src: addStaticRootToUrl("images/placeholders/image_na.png"),
          alt: "No images available",
        },
      ];
    }

    if (variantData?.productGroup?.imageUrl) {
      return [
        {
          src: variantData.productGroup.imageUrl,
          alt: variantData.productGroup.name || product?.itemName || "Product",
        },
      ];
    }

    return product ? getAllProductImages(product) : [];
  }, [allWithinGroupSelected, pricingData, product, variantData?.productGroup]);

  const carouselImages = useMemo(() => {
    if (images.length > 0) return images;
    return [
      {
        src: addStaticRootToUrl("images/placeholders/image_na.png"),
        alt: "No images available",
      },
    ];
  }, [images]);

  const displayProduct = useMemo(() => {
    if (!product) return null;
    if (
      allWithinGroupSelected &&
      pricingData.length > 0 &&
      pricingData[0].productItemName
    ) {
      const manufacturerName =
        product.manufacturerName ||
        pricingData[0].productManufacturer ||
        product.manufacturer.name;
      return {
        id: product.id,
        itemName: pricingData[0].productItemName,
        itemNumber: pricingData[0].productItemNumber || product.itemNumber,
        description: pricingData[0].productDescription || product.description,
        manufacturer: { name: manufacturerName },
      };
    }
    // Show the canonical name for the currently-active sibling group so the
    // page title updates when the user navigates between groups.
    if (currentGroupId && siblingGroupData?.groups) {
      const currentGroup = siblingGroupData.groups.find(
        (g) => g.groupId === currentGroupId,
      );
      if (currentGroup?.canonicalName) {
        const mfrName =
          product.manufacturerName || product.manufacturer?.name || "";
        return {
          ...product,
          itemName: currentGroup.canonicalName,
          manufacturer: { ...product.manufacturer, name: mfrName },
        };
      }
    }
    if (product.manufacturerName) {
      return {
        ...product,
        manufacturer: {
          ...product.manufacturer,
          name: product.manufacturerName,
        },
      };
    }
    return product;
  }, [
    allWithinGroupSelected,
    pricingData,
    product,
    currentGroupId,
    siblingGroupData?.groups,
  ]);

  const primaryDistributorProduct = useMemo(() => {
    if (pricingData.length > 0) return pricingData[0];
    if (product && hasDistributorProducts(product)) {
      const preferred = product.distributorProducts.find(
        (dp) => dp.distributor?.isPreferred,
      );
      return preferred || product.distributorProducts[0] || null;
    }
    return null;
  }, [pricingData, product]);

  const displayManufacturerSku = useMemo(() => {
    const manufacturerSku = primaryDistributorProduct?.manufacturerCode?.trim();
    return manufacturerSku || null;
  }, [primaryDistributorProduct]);

  // Whether pricing should be shown without explicit variant selection.
  // True for: standalones (no siblings), or when the group context comes pre-populated from URL.
  const groupContextPrePopulated = useMemo(() => {
    if (!siblingGroupData) return false;
    if (siblingGroupData.isStandalone) return true;
    // Pre-populated if all group-level attributes are already selected
    return groupAttributeKeys.every((k) => k in selectedGroupContext);
  }, [siblingGroupData, groupAttributeKeys, selectedGroupContext]);

  // Effects
  useEffect(() => {
    if (!productId) {
      setError("Product ID is required");
      setIsLoading(false);
      return;
    }
    if (isUserLoading) return;
    if (!currentUser?.id) return;

    const fetchProductDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await ProductDetailApi.getProductDetail(productId);
        const prod = response.body;
        if (prod) setProduct(prod);
        else setError("Product not found");
      } catch (err) {
        console.error("Failed to fetch product details:", err);
        setError("Failed to load product details");
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSimilarProducts = async () => {
      try {
        setIsLoadingSimilarProducts(true);
        const response = await ProductApi.getSimilarProducts(productId, {
          pageSize: 5,
        });
        setSimilarProducts(response.body.results);
      } catch (err) {
        console.error("Failed to fetch similar products:", err);
      } finally {
        setIsLoadingSimilarProducts(false);
      }
    };

    fetchProductDetails();
    fetchSimilarProducts();
  }, [productId, isUserLoading, currentUser?.id]);

  // Fetch sibling groups when formulation changes (provides group-level chip options).
  // Re-fetching with the current formulation as context lets the backend identify
  // the correct currentGroupId and currentGroupVariantAttributes for this formulation.
  useEffect(() => {
    if (!productId || isUserLoading || !currentUser?.id) return;

    const formulation = (selectedFilters.formulation as string) || "";
    const context: Record<string, string> = {};
    if (formulation) context.formulation = formulation;

    setSiblingGroupData(null);
    ProductDetailApi.getSiblingGroups(productId, context)
      .then((res) => setSiblingGroupData(res.body))
      .catch(() => setSiblingGroupData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isUserLoading, currentUser?.id, selectedFilters.formulation]);

  useEffect(() => {
    if (isUserLoading || !currentUser?.id) return;
    if (savedForLaters !== null) return;
    fetchSavedForLater();
  }, [fetchSavedForLater, isUserLoading, currentUser?.id, savedForLaters]);

  // Fetch within-group variants whenever product, groupId, or within-group filters change.
  // Also passes group_id so the backend returns the correct sibling group's members.
  useEffect(() => {
    if (!productId || !product) return;
    if (isUserLoading || !currentUser?.id) return;

    const fetchVariants = async () => {
      try {
        setIsLoadingVariants(true);
        // Build filter params: only within-group keys + group_id for disambiguation.
        const variantFilters: Record<string, string | number> = {
          ...selectedWithinGroup,
        };
        if (currentGroupId) {
          variantFilters["group_id"] = currentGroupId;
        }
        const response = await ProductDetailApi.getProductVariants(
          productId,
          variantFilters,
        );
        setVariantData(response.body);
      } catch (err) {
        console.error("Failed to fetch product variants:", err);
        setVariantData(null);
      } finally {
        setIsLoadingVariants(false);
      }
    };

    if (currentGroupId) {
      fetchVariants();
    } else {
      setVariantData(null);
    }
  }, [
    productId,
    product,
    isUserLoading,
    currentUser?.id,
    currentGroupId,
    JSON.stringify(selectedWithinGroup),
  ]);

  // Sync selectedFilters → URL query params on every change (replace, not push).
  useEffect(() => {
    const current = Object.fromEntries(searchParams.entries());
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(selectedFilters)) {
      if (v !== "" && v != null) next[k] = String(v);
    }
    // Only update if something actually changed to avoid infinite loops.
    const changed =
      Object.keys(next).length !== Object.keys(current).length ||
      Object.entries(next).some(([k, v]) => current[k] !== v);
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [selectedFilters]);

  // Callbacks
  const handleBackClick = useCallback(() => navigate(-1), [navigate]);

  const handleFilterChange = useCallback(
    (filterKey: string, value: string | number, isAvailable: boolean) => {
      const isGroupLevel = groupAttributeKeys.includes(filterKey);

      if (isGroupLevel) {
        // Group-level chip changed: keep other group-level selections, clear within-group.
        if (filterKey === groupFormKey || !isAvailable) {
          // Form change or unavailable selection resets all other selections.
          setSelectedFilters({ [filterKey]: value });
        } else {
          setSelectedFilters((prev) => {
            const next: Record<string, string | number> = {};
            for (const k of groupAttributeKeys) {
              if (k === filterKey) next[k] = value;
              else if (k in prev) next[k] = prev[k];
            }
            return next;
          });
        }
      } else {
        // Within-group chip changed.
        if (isAvailable) {
          setSelectedFilters((prev) => ({ ...prev, [filterKey]: value }));
        } else {
          // Unavailable option: keep group-level context, reset within-group to just this key.
          setSelectedFilters((prev) => {
            const next: Record<string, string | number> = {};
            for (const k of groupAttributeKeys) {
              if (k in prev) next[k] = prev[k];
            }
            next[filterKey] = value;
            return next;
          });
        }
      }
    },
    [groupAttributeKeys, groupFormKey],
  );

  // Render
  if (isLoading) {
    return (
      <div className="flex-row flex-center padding-xxl">
        <SpinningLoader />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="padding-xxl">
        <div className="text-center">
          <h1 className="text-xl margin-bottom-md">Product Not Found</h1>
          <p className="margin-bottom-lg">
            {error || "The requested product could not be found."}
          </p>
          <Button onClick={handleBackClick}>Go Back</Button>
        </div>
      </div>
    );
  }

  if (!displayProduct) return null;

  const groupAttrDisplayNames = siblingGroupData?.attributeDisplayNames ?? {};
  const groupAttrOptions = siblingGroupData?.groupAttributeOptions ?? {};
  const groupAttrOrder = ["formulation", "strength", "case_quantity", "count"];
  const numericAttrs = new Set(["count", "case_quantity"]);

  function sortAttrValues(key: string, values: string[]): string[] {
    if (key === "strength") {
      return [...values].sort((a, b) => {
        const na = parseFloat(a);
        const nb = parseFloat(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.localeCompare(b);
      });
    }
    if (numericAttrs.has(key)) {
      return [...values].sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.localeCompare(b);
      });
    }
    return values;
  }

  const orderedGroupAttrOptions = Object.entries(groupAttrOptions)
    // Only show attributes that exist in groups matching the currently-selected
    // formulation (e.g. hide "Volume" when "Chewable Tablet" is the formulation).
    .filter(([key]) => !relevantGroupAttrKeys || relevantGroupAttrKeys.has(key))
    .sort(([a], [b]) => {
      const ai = groupAttrOrder.indexOf(a);
      const bi = groupAttrOrder.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map(
      ([key, values]) =>
        [key, sortAttrValues(key, values)] as [string, string[]],
    );

  return (
    <div className="padding-xxl">
      <div className="margin-bottom-lg flex-row space-between flex-center">
        {!!productId && <ProductBreadcrumbs productId={productId} />}

        <RequiresRoleContainer role={Roles.SiteAdmin}>
          <a
            href={`/admin/app/product/${productId}/`}
            target="_blank"
            data-testid="admin-edit-link"
          >
            <i className="fa-solid fa-pen-to-square" />
            Edit
          </a>
        </RequiresRoleContainer>
      </div>

      <div className={styles.productDetailContainer}>
        {!!productId && (
          <FavoriteHeartButton
            productId={productId}
            variant="pdp"
            className={styles.cornerHeart}
          />
        )}
        <div className={styles.productImage}>
          <ImageCarousel
            key={`${productId ?? "product"}-${carouselImages.length}`}
            images={carouselImages}
            imageHeight={380}
            imageWidth={380}
            placeholderImage={{
              src: addStaticRootToUrl("images/placeholders/image_na.png"),
              srcSet: `${addStaticRootToUrl("images/placeholders/image_na.png")} 1x, ${addStaticRootToUrl("images/placeholders/image_na@2x.png")} 2x, ${addStaticRootToUrl("images/placeholders/image_na@3x.png")} 3x`,
              alt: "Image not available",
            }}
            onImageChange={() => {}}
            onImageError={(index: number, err: Error) => {
              console.warn("Image failed to load at index:", index, err);
            }}
            data-testid={`product-image-carousel-${productId}`}
          />
        </div>

        <div className={styles.productInfo}>
          <h1 className={styles.productTitle}>{product.itemName}</h1>
          <p className={styles.manufacturerName}>
            {displayProduct.manufacturer.name} | SKU:{" "}
            {displayManufacturerSku || "N/A"}
          </p>

          {displayProduct?.description && (
            <ProductDescription
              className="margin-top-lg text-md"
              description={displayProduct.description}
            />
          )}

          {variantData?.productGroup && (
            <div className="margin-top-lg">
              <div className={styles.productDetailsGrid}>
                {/* {variantData.productGroup.brand && (
                  <div className={styles.detailItem}>
                    <label>Brand:</label>
                    <span>{variantData.productGroup.brand}</span>
                  </div>
                )} */}
                {variantData.productGroup.activeIngredient && (
                  <div className={styles.detailItem}>
                    <label>Active Ingredient:</label>
                    <span
                      id={`active-ingredient-${kebabCase(variantData.productGroup.activeIngredient)}`}
                    >
                      {variantData.productGroup.activeIngredient}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasVariantData && (
            <div className="margin-top-lg">
              {/* Group-level chips: navigate between sibling groups (formulation, strength, etc.) */}
              <div className="spaced-content-v">
                {orderedGroupAttrOptions.map(([filterKey, allOptions]) => {
                  const label = groupAttrDisplayNames[filterKey] || filterKey;
                  const selectedValue = selectedFilters[filterKey];
                  const availableGroupOptions =
                    computeGroupAttrAvailableOptions(
                      filterKey,
                      allOptions,
                      selectedGroupContext,
                      siblingGroupData?.groups ?? [],
                    );
                  return (
                    <VariantChipSelector
                      key={filterKey}
                      label={label}
                      allOptions={allOptions}
                      availableOptions={availableGroupOptions}
                      selectedValue={selectedValue}
                      onSelect={(value, isAvailable) => {
                        if (String(value) === String(selectedValue)) {
                          setSelectedFilters((prev) => {
                            const { [filterKey]: _, ...rest } = prev;
                            return rest;
                          });
                          return;
                        }
                        handleFilterChange(
                          filterKey,
                          value,
                          isAvailable ?? true,
                        );
                      }}
                    />
                  );
                })}
              </div>

              {/* Within-group chips: select variant within the current group (count, case_quantity, etc.) */}
              <div className={styles.variantFiltersContainer}>
                {Object.entries(normalizedFilterOptions).map(
                  ([filterKey, allOptions]) => {
                    const label =
                      variantData!.attributeLabels?.[filterKey] || filterKey;
                    const selectedValue = selectedFilters[filterKey];

                    const { available: availableOptions } =
                      computeAvailableOptions(
                        filterKey,
                        allOptions,
                        selectedWithinGroup,
                        variantData!.memberVariants || [],
                      );

                    return (
                      <VariantChipSelector
                        key={filterKey}
                        label={label}
                        allOptions={allOptions}
                        availableOptions={availableOptions}
                        selectedValue={selectedValue}
                        onSelect={(value, isAvailable) => {
                          if (String(value) === String(selectedValue)) {
                            setSelectedFilters((prev) => {
                              const { [filterKey]: _, ...rest } = prev;
                              return rest;
                            });
                            return;
                          }
                          handleFilterChange(filterKey, value, isAvailable);
                        }}
                      />
                    );
                  },
                )}
              </div>
            </div>
          )}

          {/* {!variantData && (
            <div className="margin-top-lg">
              <div className={styles.productDetailsGrid}>
                <div className={styles.detailItem}>
                  <label>Unit of Measure:</label>
                  <span>{product.unitOfMeasure}</span>
                </div>
                <div className={styles.detailItem}>
                  <label>Package Quantity:</label>
                  <span>{product.packageQuantity}</span>
                </div>
                {"prop65" in product && product.prop65 && (
                  <div className={styles.detailItem}>
                    <label>Prop 65:</label>
                    <span>{product.prop65}</span>
                  </div>
                )}
              </div>
            </div>
          )} */}
        </div>
      </div>

      {isLoadingVariants ? (
        <div className={styles.bottomTableContainer}>
          <div className="flex-row flex-center padding-xl">
            <SpinningLoader />
          </div>
        </div>
      ) : !matchingMember && hasVariantData && !groupContextPrePopulated ? (
        <div className={styles.bottomTableContainer}>
          <div
            className="padding-xl text-center"
            style={{ backgroundColor: "#f5f5f5", borderRadius: "8px" }}
          >
            <p className="text-md" style={{ color: "#666" }}>
              Please select all product options to view pricing and
              availability.
            </p>
          </div>
        </div>
      ) : pricingData.length > 0 ? (
        <div className={styles.bottomTableContainer}>
          {isLoadingVariants && (
            <div className="flex-row flex-center padding-md">
              <SpinningLoader />
            </div>
          )}
          <ProductPricingTable
            tableHeading="Pricing Details"
            data={pricingData.map((r) =>
              mapDistributorProductToTableRow(
                r,
                distributorApiHealthStatuses,
                getDistributorConnection,
              ),
            )}
          />
        </div>
      ) : null}

      <div className={styles.productDetailsTablesContainer}>
        <div className={styles.detailsTablesContainer}>
          <div>
            <DetailsTable
              tableHeading="Product Details"
              rows={[
                {
                  label: "Manufacturer",
                  value: product.manufacturer.name,
                },
                // { label: "Species", value: "Feline, Canine" },
                {
                  label: "Ingredients",
                  value: (
                    <span
                      id={`product-details-ingredients`}
                      className="spaced-content"
                    >
                      {map(
                        product.activeIngredients,
                        ({ ingredientName }, index) => (
                          <Link
                            to={`/search?${getParamString("filters", JSON.stringify({ ingredients: [ingredientName] }))}`}
                            key={ingredientName}
                          >
                            <span
                              className="text-capitalize"
                              dangerouslySetInnerHTML={{
                                __html: ingredientName,
                              }}
                            />
                            {index < product.activeIngredients.length - 1 &&
                              ", "}
                          </Link>
                        ),
                      )}
                    </span>
                  ),
                },
                { label: "Size", value: product.unitOfMeasure || "N/A" },
                {
                  label: "Form",
                  value: (
                    <span className="text-capitalize">
                      {(selectedFilters.formulation as string) || "N/A"}
                    </span>
                  ),
                },
                {
                  label: "Packaging",
                  value: selectedFilters.count
                    ? `${selectedFilters.count} count`
                    : "N/A",
                },
                {
                  label: "Category",
                  value: "Pharmaceutical/Anti-inflammatory",
                },
              ]}
              data-testid="product-details-table"
            />
          </div>

          <div>
            <DetailsTable
              tableHeading="Supporting Documents"
              singleColumn={true}
              rows={[
                {
                  label: "Safety Data Sheet",
                  url: "sdsUrl" in product ? product.sdsUrl : undefined,
                },
                {
                  label: "MSDS",
                  url: "msdsUrl" in product ? product.msdsUrl : undefined,
                },
                {
                  label: "Compendium",
                  url:
                    "compendiumUrl" in product
                      ? product.compendiumUrl
                      : undefined,
                },
              ]
                .filter((doc) => doc.url)
                .map((doc) => ({
                  label: (
                    <span id={`supporting-doc-label-${kebabCase(doc.label)}`}>
                      {doc.label}
                    </span>
                  ),
                  value: "",
                  link: doc.url!,
                  icon: (
                    <i
                      id={`supporting-doc-icon-${kebabCase(doc.label)}`}
                      className="fa-light fa-arrow-up-right-from-square"
                      aria-hidden="true"
                    />
                  ),
                }))}
              data-testid="supporting-documents-table"
            />
          </div>
        </div>
      </div>

      <div className="position-relative">
        {isLoadingSimilarProducts && <SpinningLoader />}
        {similarProducts && similarProducts.length > 0 && (
          <div className="padding-top-xxl">
            <SectionHeader
              header="Similar Products"
              rightSideContent={
                <Link
                  id="pdp-view-all-similar-link"
                  className="text-xs"
                  to={`/product/${productId}/similar-products`}
                >
                  View All
                </Link>
              }
            />
            <ProductListRow
              products={similarProducts}
              tileClassName="height-initial-important"
              imageSizePx={200}
            />
          </div>
        )}
      </div>
    </div>
  );
}
